import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { CMECapabilities } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { CMEResponse } from '../../../NonReact/Types/Types'
import { IsStringEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods, ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { ANY_NUMBER_OF_TELSYS_AVAILABLE, CapabilityBitFlags } from '../Constants'
import {
    CapabilitiesFieldNames,
    cxaBroadworksFieldName,
    cxaTeamsFieldName,
    cxaWebexFieldName,
    cxiBroadworksFieldName,
    cxiTeamsFieldName,
    cxiWebexFieldName,
    enterpriseFieldName,
    legacyFieldName,
    legacyTelephonyFieldName,
    maxTelsysCountFieldName
} from '../FieldName'
import { CapabilitiesEnabled } from '../Types'
import { TelSysTypes } from '../../TelSysConfiguration/Constants/Constants'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import FormRow from '../../Common/Components/FormRow'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'

type Props = {
    cmeID?: string
    isViewOnly?: boolean
    isCxiEnabledForPlatform: Record<TelSysTypes, boolean>
    isCxiEnabledOnAnyPlatform: boolean
    isAdmin: boolean
    parentCME: Nullable<CMEResponse>
}

export const Capabilities: FC<Props> = ({
    cmeID,
    isViewOnly,
    isCxiEnabledForPlatform,
    isCxiEnabledOnAnyPlatform,
    isAdmin,
    parentCME
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CMECapabilities.NAME)

    const isNewCME: boolean = IsStringEmpty(cmeID)

    const { watch, setValue, trigger }: UseFormMethods = useFormContext()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [capabilitiesEnabled, setCapabilitiesEnabled]: ReactState<Nullable<CapabilitiesEnabled>> =
        useState<Nullable<CapabilitiesEnabled>>()

    const isRootCME: boolean = cmeID === DEFAULT_CME_ID

    const cxaBroadworksValue: boolean = watch(cxaBroadworksFieldName, false)
    const cxaWebexValue: boolean = watch(cxaWebexFieldName, false)
    const cxaTeamsValue: boolean = watch(cxaTeamsFieldName, false)

    const legacyTelphonyValue: boolean = watch(legacyTelephonyFieldName, false)
    const legacyValue: boolean = watch(legacyFieldName, false)
    const enterpriseValue: boolean = watch(enterpriseFieldName, false)

    const legacyLicenceDisabled: boolean = useMemo(
        (): boolean => !cxaBroadworksValue && !!cxaWebexValue && !legacyTelphonyValue,
        [cxaBroadworksValue, cxaWebexValue, legacyTelphonyValue]
    )

    const licencesDisabled: boolean = useMemo(
        (): boolean => !cxaBroadworksValue && !cxaWebexValue && !legacyTelphonyValue,
        [cxaBroadworksValue, cxaWebexValue, legacyTelphonyValue]
    )

    useEffect((): void => {
        if (isVarNotEmpty(parentCME?.Capabilities)) {
            const parentCapabilities: CapabilitiesEnabled = {
                cxaBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_BROADWORKS),
                cxaWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_WEBEX),
                cxaTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_TEAMS),
                cxiBroadworks: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_BROADWORKS),
                cxiWebex: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_WEBEX),
                cxiTeams: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXI_TEAMS),
                legacyTelephony: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.LEGACY_TELEPHONY),
                legacy: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.CXA_LEGACY),
                enterprise: isBitmaskMatching(parentCME.Capabilities, CapabilityBitFlags.ENTERPRISE)
            }

            if (isMounted.current) {
                setCapabilitiesEnabled(parentCapabilities)

                const setCapabilityValue = (capability: string, isEnabled: boolean): void => {
                    if (!isEnabled || isNewCME) {
                        setValue(capability, isEnabled)
                    }
                }

                setTimeout((): void => {
                    setCapabilityValue(cxaBroadworksFieldName, parentCapabilities.cxaBroadworks)
                    setCapabilityValue(cxaWebexFieldName, parentCapabilities.cxaWebex)
                    setCapabilityValue(legacyTelephonyFieldName, parentCapabilities.legacyTelephony)
                    setCapabilityValue(legacyFieldName, parentCapabilities.legacy)
                    setCapabilityValue(enterpriseFieldName, parentCapabilities.enterprise)
                    setCapabilityValue(cxaTeamsFieldName, parentCapabilities.cxaTeams)
                    setCapabilityValue(cxiBroadworksFieldName, parentCapabilities.cxiBroadworks)
                    setCapabilityValue(cxiTeamsFieldName, parentCapabilities.cxiTeams)
                    setCapabilityValue(cxiWebexFieldName, parentCapabilities.cxiWebex)
                })
            }
        } else if (isMounted.current) {
            setCapabilitiesEnabled({
                cxaBroadworks: true,
                cxaWebex: true,
                cxaTeams: true,
                cxiBroadworks: true,
                cxiWebex: true,
                cxiTeams: true,
                legacyTelephony: true,
                legacy: true,
                enterprise: true
            })
        }
    }, [parentCME, isNewCME])

    const onChange = (): void => {
        if (watch(cxaWebexFieldName) || watch(cxaTeamsFieldName)) {
            setValue(enterpriseFieldName, true)

            if (!watch(cxaBroadworksFieldName) && !watch(legacyTelephonyFieldName)) {
                setValue(legacyFieldName, false)
            }
        }

        if (
            !watch(legacyTelephonyFieldName) &&
            !watch(cxaWebexFieldName) &&
            !watch(cxaBroadworksFieldName) &&
            !watch(cxaTeamsFieldName)
        ) {
            setValue(enterpriseFieldName, false)
            setValue(legacyFieldName, false)
        }

        setTimeout((): void => {
            trigger(legacyFieldName)
            trigger(enterpriseFieldName)
        })
    }

    const isCxiCapabilityCheckboxDisabled = (capability: CapabilitiesFieldNames): boolean => {
        if (isAdmin && isRootCME) {
            return false
        }

        const isCapabilityEnabled: boolean = capabilitiesEnabled?.[capability as keyof CapabilitiesEnabled] ?? false
        return !isCapabilityEnabled
    }

    const resetToAny =
        (isResetOnEmpty?: boolean): ((value?: string) => void) =>
        (value?: string): void => {
            if (
                isNaN(Number(value)) ||
                (isResetOnEmpty && IsStringEmpty(value)) ||
                (isVarNotEmpty(value) && !isNaN(Number(value)) && Number(value) < 0)
            ) {
                setValue(maxTelsysCountFieldName, ANY_NUMBER_OF_TELSYS_AVAILABLE)
            }
        }

    return (
        <div className='capabilities-tab'>
            <div className='ct-max-telsys-count'>
                <FormRow>
                    <OutlinedTextInput
                        inputName={maxTelsysCountFieldName}
                        labelLeft={language.get(CMECapabilities.MaxTelsysCount)}
                        onClick={({ currentTarget }: React.MouseEvent<Element, MouseEvent>): void => {
                            currentTarget?.querySelector('input')?.select()
                        }}
                        onChange={resetToAny(false)}
                        onBlur={resetToAny(true)}
                        disabled={!!isViewOnly}
                    />
                </FormRow>
            </div>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.AvailableTelServers)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        <th className='header-centre'>{language.get(CMECapabilities.CxAnalytics)}</th>
                        {isCxiEnabledOnAnyPlatform ? (
                            <th className='header-centre'>{language.get(CMECapabilities.CxIntegration)}</th>
                        ) : null}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{language.get(CMECapabilities.Broadworks)}</td>
                        <td>
                            <RouterCheckbox
                                name={cxaBroadworksFieldName}
                                disabled={!capabilitiesEnabled?.cxaBroadworks || !!isViewOnly}
                                onChange={(): void => {
                                    onChange()

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                            />
                        </td>
                        {isCxiEnabledOnAnyPlatform ? (
                            <td>
                                {isCxiEnabledForPlatform[TelSysTypes.BROADSOFT_BROADWORKS] ? (
                                    <RouterCheckbox
                                        name={cxiBroadworksFieldName}
                                        disabled={
                                            isCxiCapabilityCheckboxDisabled(CapabilitiesFieldNames.CXI_BROADWORKS) ||
                                            !!isViewOnly
                                        }
                                        onChange={(): void => {
                                            onChange()

                                            setTimeout((): void => {
                                                trigger(legacyFieldName)
                                                trigger(enterpriseFieldName)
                                            })
                                        }}
                                    />
                                ) : null}
                            </td>
                        ) : null}
                    </tr>
                    <tr>
                        <td>{language.get(CMECapabilities.Webex)}</td>
                        <td>
                            <RouterCheckbox
                                name={cxaWebexFieldName}
                                disabled={!capabilitiesEnabled?.cxaWebex || !!isViewOnly}
                                onChange={(): void => {
                                    onChange()

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                            />
                        </td>
                        {isCxiEnabledOnAnyPlatform ? (
                            <td>
                                {isCxiEnabledForPlatform[TelSysTypes.WEBEX_CALLING] ? (
                                    <RouterCheckbox
                                        name={cxiWebexFieldName}
                                        disabled={
                                            isCxiCapabilityCheckboxDisabled(CapabilitiesFieldNames.CXI_WEBEX) ||
                                            !!isViewOnly
                                        }
                                        onChange={(): void => {
                                            onChange()

                                            setTimeout((): void => {
                                                trigger(legacyFieldName)
                                                trigger(enterpriseFieldName)
                                            })
                                        }}
                                    />
                                ) : null}
                            </td>
                        ) : null}
                    </tr>
                    <tr>
                        <td>{language.get(CMECapabilities.Teams)}</td>
                        <td>
                            <RouterCheckbox
                                name={cxaTeamsFieldName}
                                disabled={!capabilitiesEnabled?.cxaTeams || !!isViewOnly}
                                onChange={(): void => {
                                    onChange()

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                            />
                        </td>
                        {isCxiEnabledOnAnyPlatform ? (
                            <td>
                                {isCxiEnabledForPlatform[TelSysTypes.MS_TEAMS] ? (
                                    <RouterCheckbox
                                        name={cxiTeamsFieldName}
                                        disabled={
                                            isCxiCapabilityCheckboxDisabled(CapabilitiesFieldNames.CXI_TEAMS) ||
                                            !!isViewOnly
                                        }
                                        onChange={(): void => {
                                            onChange()

                                            setTimeout((): void => {
                                                trigger(legacyFieldName)
                                                trigger(enterpriseFieldName)
                                            })
                                        }}
                                    />
                                ) : null}
                            </td>
                        ) : null}
                    </tr>
                    <tr>
                        <td>{LanguageUtils.formatLanguageEntry(language.get(CMECapabilities.LegacyTelephony))}</td>
                        <td>
                            <RouterCheckbox
                                name={legacyTelephonyFieldName}
                                disabled={!capabilitiesEnabled?.legacyTelephony || !!isViewOnly}
                                onChange={(value: boolean): void => {
                                    onChange()

                                    if (value) {
                                        setValue(legacyFieldName, true)
                                    }

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                            />
                        </td>
                        {isCxiEnabledOnAnyPlatform ? <td></td> : null}
                    </tr>
                </tbody>
            </table>
            <Heading level={HeadingLevel.TWO} className='ct-title'>
                {language.get(CMECapabilities.AvailableLicences)}
            </Heading>
            <table className='ct-table'>
                <thead>
                    <tr>
                        <th>{language.get(CMECapabilities.Capability)}</th>
                        <th className='header-centre'>{language.get(CMECapabilities.CapabilityEnabled)}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{LanguageUtils.formatLanguageEntry(language.get(CMECapabilities.Legacy))}</td>
                        <td>
                            <RouterCheckbox
                                name={legacyFieldName}
                                disabled={
                                    legacyLicenceDisabled ||
                                    licencesDisabled ||
                                    !capabilitiesEnabled?.legacy ||
                                    !!isViewOnly
                                }
                                onChange={() => {
                                    trigger(legacyFieldName)

                                    setTimeout((): void => {
                                        trigger(enterpriseFieldName)
                                    })
                                }}
                                rules={{
                                    validate: {
                                        isLicenceTypeRequired: (value: boolean): ValidateResult => {
                                            if (
                                                (cxaBroadworksValue ||
                                                    cxaWebexValue ||
                                                    cxaTeamsValue ||
                                                    legacyTelphonyValue) &&
                                                !value &&
                                                !enterpriseValue
                                            ) {
                                                return false
                                            }

                                            return true
                                        }
                                    }
                                }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>{language.get(CMECapabilities.Enterprise)}</td>
                        <td>
                            <RouterCheckbox
                                name={enterpriseFieldName}
                                disabled={licencesDisabled || !capabilitiesEnabled?.enterprise || !!isViewOnly}
                                onChange={(): void => {
                                    trigger(enterpriseFieldName)

                                    setTimeout((): void => {
                                        trigger(legacyFieldName)
                                    })
                                }}
                                rules={{
                                    validate: {
                                        isLicenceTypeRequired: (value: boolean): ValidateResult => {
                                            if (
                                                (cxaBroadworksValue ||
                                                    cxaWebexValue ||
                                                    cxaTeamsValue ||
                                                    legacyTelphonyValue) &&
                                                !value &&
                                                !legacyValue
                                            ) {
                                                return false
                                            }

                                            return true
                                        }
                                    }
                                }}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}
